/**
 * Design Systems registry — the authoritative definitions of the three MVP
 * style languages (Modern, Classic, Premium).
 *
 * Source of truth: `docs/aiWebsiteBuilder/DESIGN_SYSTEMS.md`. This file must match
 * that document exactly.
 *
 * A Design System is a *style language*, not branding and not a color theme:
 *   - It defines typography, corner rounding, spacing rhythm, navigation style,
 *     button style, and visual tone.
 *   - It defines NO colors (the MVP is light-only; color comes from Branding/Theme).
 *   - It defines NO layout (layout is owned by component variants).
 *
 * The design system provides the DEFAULT style tokens; the schema `Theme` stores a
 * site's effective values seeded from these defaults (see DESIGN_SYSTEMS.md).
 * `navigationStyle`, `buttonStyle`, and `visualTone` are design-system-only
 * attributes read directly by the Builder Engine — they are intentionally NOT part
 * of the platform-independent schema contract.
 *
 * Registry layer only: no React, no rendering, no AI.
 */
import type {
  DesignSystem,
  BorderRadiusScale,
  SpacingScale,
  SupportedFont,
} from '../schema'

/** All design system ids, in canonical order. */
export const DESIGN_SYSTEM_IDS = ['modern', 'classic', 'premium'] as const

/**
 * How navigation bars are styled under a design system. Distinct from a navbar
 * *variant* (which is layout): this is the aesthetic treatment the Builder Engine
 * applies. Read by the engine, not stored in the schema.
 */
export type NavigationStyle = 'minimal' | 'bordered' | 'floating'

/**
 * How buttons are styled under a design system. Read by the engine, not stored in
 * the schema.
 */
export type ButtonStyle = 'solid' | 'outline' | 'soft'

/** The typography pairing a design system applies by default. */
export interface DesignSystemTypography {
  readonly headingFont: SupportedFont
  readonly bodyFont: SupportedFont
}

/**
 * The complete definition of one design system.
 *
 * Future extension points: add optional style attributes (e.g. `elevation`,
 * `motion`) additively; add new systems by extending `DesignSystem` + this map.
 */
export interface DesignSystemDefinition {
  readonly id: DesignSystem
  /** One-line design philosophy. */
  readonly philosophy: string
  /** Default heading/body fonts (from the supported set). */
  readonly typography: DesignSystemTypography
  /** Default corner-radius token. */
  readonly borderRadius: BorderRadiusScale
  /** Default spacing/density token. */
  readonly spacing: SpacingScale
  /** How navigation bars are styled. */
  readonly navigationStyle: NavigationStyle
  /** How buttons are styled. */
  readonly buttonStyle: ButtonStyle
  /** Short human description of the overall tone. */
  readonly visualTone: string
}

/**
 * The design system registry. Keyed by {@link DesignSystem}; the `Record` type
 * guarantees every design system is defined.
 */
export const DESIGN_SYSTEMS: Record<DesignSystem, DesignSystemDefinition> = {
  modern: {
    id: 'modern',
    philosophy:
      'Clean, minimal, and spacious. Contemporary and tech-forward, built on generous whitespace and a single sans-serif voice.',
    typography: { headingFont: 'Poppins', bodyFont: 'Inter' },
    borderRadius: 'large',
    spacing: 'spacious',
    navigationStyle: 'minimal',
    buttonStyle: 'soft',
    visualTone: 'Fresh, approachable, current.',
  },
  classic: {
    id: 'classic',
    philosophy:
      'Timeless and editorial. Trustworthy and established, pairing a serif heading voice with a comfortable reading body.',
    typography: { headingFont: 'Playfair Display', bodyFont: 'Lora' },
    borderRadius: 'small',
    spacing: 'normal',
    navigationStyle: 'bordered',
    buttonStyle: 'solid',
    visualTone: 'Elegant, dependable, editorial.',
  },
  premium: {
    id: 'premium',
    philosophy:
      'Refined luxury. High-contrast typography, sharp geometry, and expansive spacing that signal quality and exclusivity.',
    typography: { headingFont: 'Fraunces', bodyFont: 'Montserrat' },
    borderRadius: 'none',
    spacing: 'spacious',
    navigationStyle: 'floating',
    buttonStyle: 'outline',
    visualTone: 'Sophisticated, high-end, minimal-luxury.',
  },
}

/** Look up a design system definition. */
export function getDesignSystem(id: DesignSystem): DesignSystemDefinition {
  return DESIGN_SYSTEMS[id]
}
